import React from 'react'
import { styled } from '@mui/system'
import { fontSizes, colors } from '../../../Components/consts'
import { Percent1Digit } from '../../../Utils/formatting'
import { FilteredResult } from '../../../Constants/statsProcess'
import { ResponsiveLabel } from './twoSizeLabel'
import type { ResponsiveLabelProps } from './twoSizeLabel'

type ValueProps = {
  value: string | number | null | undefined | Date
  percent?: boolean
  label: string
  color?: string
}

type StyleProps = { color?: string; noBorder?: boolean }

export const ValInRowCon = styled('div')<StyleProps>(({ noBorder, theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  margin: theme.spacing(0.5, 0),
  '& > div': {
    paddingRight: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      paddingRight: theme.spacing(2),
    },
    ...(noBorder && {
      borderBottom: 0,
    }),
  },
}))

export const ValInColCon = styled('div')(() => ({
  '& > div': {
    '&:last-child': {
      borderBottom: 0,
      paddingBottom: 0,
      marginBottom: 0,
    },
  },
}))

export const Val = styled('div')<StyleProps>(({ color, theme }) => ({
  borderBottom: `1px solid ${colors.lightestGrey}`,
  marginBottom: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  '& .value': {
    fontWeight: 'bold',
    color: color || colors.helperText,
    fontSize: fontSizes.medium,
    [theme.breakpoints.down('sm')]: {
      fontSize: fontSizes.small,
    },
  },
  '& .label': {
    fontSize: fontSizes.small,
  },
}))

/**
 * Container for value, will not be rendered if value is null or undefined.
 * will convert numerical values to percent if `percent` attr is set
 */
export const Value: React.FC<React.PropsWithChildren<ValueProps>> = ({
  value,
  label,
  percent,
  color = colors.text,
}) => {
  if (value === null || value === undefined) {
    return null
  }
  const v =
    percent === true && typeof value === 'number'
      ? Percent1Digit(value)
      : typeof value === 'object'
      ? value.toString()
      : value
  return (
    <Val color={color}>
      <div className="value">{v}</div>
      <div className="label">{label}</div>
    </Val>
  )
}

type StatValueProps = {
  value: FilteredResult
  color?: string
  switchOn?: ResponsiveLabelProps['switchOn']
}

export const StatValue: React.FC<React.PropsWithChildren<StatValueProps>> = ({
  value,
  color = colors.text,
  switchOn,
}) => {
  if (value === null || value === undefined) {
    return null
  }
  return (
    <Val color={color}>
      <div className="value">{value.value}</div>
      <div className="label">
        <ResponsiveLabel long={value.longName} short={value.name} switchOn={switchOn} />
      </div>
    </Val>
  )
}

type ValuesInRowContainerProps = {
  noBorder?: boolean
  values?: FilteredResult[]
  color?: StatValueProps['color']
  switchOn?: ResponsiveLabelProps['switchOn']
}

/**
 * Align values as tiles in row
 * additionally can render values
 */
export const ValuesInRowContainer: React.FC<
  React.PropsWithChildren<ValuesInRowContainerProps>
> = ({ values, noBorder, color, switchOn, children }) => {
  return (
    <ValInRowCon noBorder={noBorder}>
      {values &&
        values.map((v, i) => (
          <StatValue key={i} switchOn={switchOn} color={color} value={v} />
        ))}
      {children && children}
    </ValInRowCon>
  )
}

/**
 * Align values as column, last entry without bottom line
 */
export const ValuesInColumnContainer: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  return <ValInColCon>{children}</ValInColCon>
}
